import { useUser, useSignIn, useSignUp, useClerk } from '@clerk/clerk-react';
import type { User } from '../types';

export function useAuth() {
  const { user: clerkUser, isLoaded } = useUser();
  const { signOut: clerkSignOut } = useClerk();
  const { isLoaded: signInLoaded, signIn, setActive: setSignInActive } = useSignIn();
  const { isLoaded: signUpLoaded, signUp, setActive: setSignUpActive } = useSignUp();

  const user: User | null = clerkUser
    ? {
        id: clerkUser.id,
        name: clerkUser.fullName ?? clerkUser.username ?? clerkUser.primaryEmailAddress?.emailAddress ?? '',
        email: clerkUser.primaryEmailAddress?.emailAddress ?? '',
        createdAt: new Date(clerkUser.createdAt ?? Date.now()),
      }
    : null;

  const signInFn = async (email: string, password: string) => {
    if (!signInLoaded || !signIn || !setSignInActive) throw new Error('Auth not ready');
    const result = await signIn.create({ identifier: email, password });
    if (result.status === 'complete') {
      await setSignInActive({ session: result.createdSessionId });
      return result;
    }
    throw new Error('Sign in incomplete — additional verification may be required.');
  };

  const signUpFn = async (email: string, password: string, name: string) => {
    if (!signUpLoaded || !signUp || !setSignUpActive) throw new Error('Auth not ready');
    const [firstName, ...rest] = name.trim().split(' ');
    const result = await signUp.create({
      emailAddress: email,
      password,
      firstName,
      lastName: rest.join(' ') || undefined,
    });
    if (result.status === 'complete') {
      await setSignUpActive({ session: result.createdSessionId });
    }
    return result;
  };

  return {
    user,
    loading: !isLoaded,
    signIn: signInFn,
    signUp: signUpFn,
    signOut: clerkSignOut,
  };
}
